// src/Account.js
// ============================================================
// Your Scholarship Finder — Account & Subscription Management
// ============================================================
// Shows account details, plan info, linked students.
// Cancel flow: retention popup (50% one-time) → confirm → cancel at period end.
// Resubscribe available while in canceling state.
// Only the CC holder (subscription owner) sees the cancel button.
// ============================================================

import { useState, useEffect } from 'react';
import { API_URL } from './config';
import { useAuth } from './AuthContext';
import AppHeader from './AppHeader';

export default function Account() {
  const { user } = useAuth();
  const [account, setAccount] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showRetentionModal, setShowRetentionModal] = useState(false);
  const [actionLoading, setActionLoading] = useState('');
  const [successMsg, setSuccessMsg] = useState('');
  const [editingStudentId, setEditingStudentId] = useState(null);
  const [editName, setEditName] = useState('');
  const [editEmail, setEditEmail] = useState('');
  const [editError, setEditError] = useState('');
  const [editSuccess, setEditSuccess] = useState('');

  useEffect(() => {
    fetchAccount();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

    const handleEditStudent = (student) => {
    setEditingStudentId(student.id);
    setEditName(student.full_name || '');
    setEditEmail(student.email || '');
    setEditError('');
    setEditSuccess('');
  };

  const handleCancelEdit = () => {
    setEditingStudentId(null);
    setEditName('');
    setEditEmail('');
    setEditError('');
  };

  const handleSaveStudent = async (studentId) => {
    setEditError('');
    setEditSuccess('');
    setActionLoading('editStudent');
    try {
      const token = localStorage.getItem('ss_token');
      const res = await fetch(`${API_URL}/api/parent-links/update-student/${studentId}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ full_name: editName.trim(), email: editEmail.trim() }),
      });
      const data = await res.json();
      if (!res.ok) {
        setEditError(data.error || 'Failed to update student');
        setActionLoading('');
        return;
      }
      // Update local state so the UI refreshes without a full reload
      setAccount(prev => ({
        ...prev,
        linkedStudents: prev.linkedStudents.map(s =>
          s.id === studentId ? { ...s, full_name: data.student.full_name, email: data.student.email } : s
        ),
      }));
      setEditingStudentId(null);
      setEditName('');
      setEditEmail('');
      setEditSuccess(
        data.emailChanged
          ? `Updated! A new password setup email has been sent to ${data.student.email}.`
          : 'Name updated.'
      );
      setTimeout(() => setEditSuccess(''), 6000);
    } catch (err) {
      setEditError('Network error. Please try again.');
    }
    setActionLoading('');
  };

  const fetchAccount = async () => {
    try {
      const res = await fetch(`${API_URL}/api/stripe/account-details`, {
        headers: { 'Authorization': `Bearer ${user.token}` },
      });
      if (!res.ok) throw new Error('Failed to load account');
      const data = await res.json();
      setAccount(data);
    } catch (err) {
      setError('Unable to load account details.');
    } finally {
      setLoading(false);
    }
  };

  const handleCancelClick = () => {
    // If retention offer not yet used, show the retention modal
    // Otherwise go straight to cancel confirmation
    if (!account.retentionOfferUsed) {
      setShowRetentionModal(true);
    } else {
      confirmCancel();
    }
  };

  const handleAcceptOffer = async () => {
    setActionLoading('offer');
    setSuccessMsg('');
    try {
      const res = await fetch(`${API_URL}/api/stripe/apply-retention-offer`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${user.token}`,
          'Content-Type': 'application/json',
        },
      });
      if (!res.ok) {
        const data = await res.json();
        throw new Error(data.error || 'Failed to apply offer');
      }
      setShowRetentionModal(false);
      setSuccessMsg('50% discount applied to your next renewal!');
      fetchAccount();
    } catch (err) {
      setError(err.message);
    } finally {
      setActionLoading('');
    }
  };

  const confirmCancel = async () => {
    setActionLoading('cancel');
    setSuccessMsg('');
    try {
      const res = await fetch(`${API_URL}/api/stripe/cancel-subscription`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${user.token}`,
          'Content-Type': 'application/json',
        },
      });
      if (!res.ok) {
        const data = await res.json();
        throw new Error(data.error || 'Failed to cancel');
      }
      setShowRetentionModal(false);
      setSuccessMsg('Your subscription has been canceled. You\'ll keep access until your billing period ends.');
      fetchAccount();
    } catch (err) {
      setError(err.message);
    } finally {
      setActionLoading('');
    }
  };

  const handleReactivate = async () => {
    setActionLoading('reactivate');
    setSuccessMsg('');
    try {
      const res = await fetch(`${API_URL}/api/stripe/reactivate-subscription`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${user.token}`,
          'Content-Type': 'application/json',
        },
      });
      if (!res.ok) {
        const data = await res.json();
        throw new Error(data.error || 'Failed to reactivate');
      }
      setSuccessMsg('Your subscription has been reactivated!');
      fetchAccount();
    } catch (err) {
      setError(err.message);
    } finally {
      setActionLoading('');
    }
  };

  const formatDate = (d) => {
    if (!d) return '—';
    return new Date(d).toLocaleDateString('en-US', {
      month: 'long', day: 'numeric', year: 'numeric'
    });
  };

  const formatPrice = (price) => {
    if (!price) return '—';
    return `$${price.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}/yr`;
  };

  const statusDisplay = (status, cancelAtPeriodEnd) => {
    if (cancelAtPeriodEnd) return { label: 'Canceling', color: '#D97706', bg: '#FEF3C7' };
    if (status === 'active') return { label: 'Active', color: '#059669', bg: '#D1FAE5' };
    if (status === 'past_due') return { label: 'Past Due', color: '#DC2626', bg: '#FEE2E2' };
    if (status === 'canceled') return { label: 'Canceled', color: '#6B7280', bg: '#F3F4F6' };
    return { label: status || 'None', color: '#6B7280', bg: '#F3F4F6' };
  };

  if (loading) {
    return (
      <>
        <AppHeader subtitle="Account" />
        <div style={styles.page}>
          <p style={styles.loadingText}>Loading account details…</p>
        </div>
      </>
    );
  }

  const st = account ? statusDisplay(account.status, account.cancelAtPeriodEnd) : null;
  const planLabel = account?.planType === 'family' ? 'Family Plan' :
                    account?.planType === 'individual' ? 'Individual Plan' : 'No Plan';

  return (
    <>
      <AppHeader subtitle="Account" />
      <div style={styles.page}>
        <div style={styles.container}>

          {/* Success message */}
          {successMsg && (
            <div style={styles.success}>
              {successMsg}
            </div>
          )}

          {/* Error message */}
          {error && (
            <div style={styles.error}>
              {error}
              <button onClick={() => setError('')} style={styles.dismissBtn}>✕</button>
            </div>
          )}

          {/* Canceling banner */}
          {account?.cancelAtPeriodEnd && (
            <div style={styles.cancelBanner}>
              <div style={styles.cancelBannerText}>
                <strong>Your subscription ends on {formatDate(account.end)}</strong>
                <span>You and any linked students will lose access after this date.</span>
              </div>
              <button
                onClick={handleReactivate}
                disabled={actionLoading === 'reactivate'}
                style={styles.reactivateBtn}
              >
                {actionLoading === 'reactivate' ? 'Reactivating…' : 'Keep My Subscription'}
              </button>
            </div>
          )}

          {/* Account Details Card */}
          <div style={styles.card}>
            <h2 style={styles.cardTitle}>Account Details</h2>
            <div style={styles.detailGrid}>
              <div style={styles.detailRow}>
                <span style={styles.detailLabel}>Name</span>
                <span style={styles.detailValue}>{account?.fullName}</span>
              </div>
              <div style={styles.detailRow}>
                <span style={styles.detailLabel}>Email</span>
                <span style={styles.detailValue}>{account?.email}</span>
              </div>
              <div style={styles.detailRow}>
                <span style={styles.detailLabel}>Role</span>
                <span style={styles.detailValue}>
                  {account?.role === 'parent' ? 'Parent' : 'Student'}
                </span>
              </div>
            </div>
          </div>

          {/* Subscription Card */}
          {account?.status && (
            <div style={styles.card}>
              <div style={styles.cardTitleRow}>
                <h2 style={styles.cardTitle}>Subscription</h2>
                {st && (
                  <span style={{
                    ...styles.statusBadge,
                    color: st.color,
                    background: st.bg,
                  }}>
                    {st.label}
                  </span>
                )}
              </div>
              <div style={styles.detailGrid}>
                <div style={styles.detailRow}>
                  <span style={styles.detailLabel}>Plan</span>
                  <span style={styles.detailValue}>{planLabel}</span>
                </div>
                <div style={styles.detailRow}>
                  <span style={styles.detailLabel}>Current Price</span>
                  <span style={styles.detailValue}>
                    {formatPrice(account.currentPrice)}
                    {account.retentionOfferUsed && account.currentPrice && (
                      <span style={styles.discountNote}> (50% discount applied)</span>
                    )}
                  </span>
                </div>
                <div style={styles.detailRow}>
                  <span style={styles.detailLabel}>Started</span>
                  <span style={styles.detailValue}>{formatDate(account.start)}</span>
                </div>
                <div style={styles.detailRow}>
                  <span style={styles.detailLabel}>
                    {account.cancelAtPeriodEnd ? 'Access Ends' : 'Next Billing Date'}
                  </span>
                  <span style={{
                    ...styles.detailValue,
                    ...(account.cancelAtPeriodEnd ? { color: '#D97706', fontWeight: 600 } : {}),
                  }}>
                    {formatDate(account.end)}
                  </span>
                </div>
              </div>

              {/* Cancel button — only for subscription owner with active status */}
              {account.isSubscriptionOwner && account.status === 'active' && !account.cancelAtPeriodEnd && (
                <div style={styles.cancelSection}>
                  <button onClick={handleCancelClick} style={styles.cancelBtn}>
                    Cancel Subscription
                  </button>
                </div>
              )}
            </div>
          )}

          {/* Linked Students Card (parent only) */}
                    {account?.role === 'parent' && account.linkedStudents?.length > 0 && (
            <div style={styles.card}>
              <h2 style={styles.cardTitle}>Students on This Plan</h2>
              {editSuccess && (
                <div style={{ background: '#f0fdf4', color: '#166534', padding: '10px 14px', borderRadius: 8, fontSize: 14, marginBottom: 12 }}>
                  {editSuccess}
                </div>
              )}
              <div style={styles.studentList}>
                {account.linkedStudents.map((s) => (
                  <div key={s.id} style={styles.studentRow}>
                    {editingStudentId === s.id ? (
                      <div style={{ flex: 1 }}>
                        <div style={{ marginBottom: 8 }}>
                          <label style={{ fontSize: 13, color: '#6b7280', display: 'block', marginBottom: 3 }}>Name</label>
                          <input
                            type="text"
                            value={editName}
                            onChange={(e) => setEditName(e.target.value)}
                            style={{ width: '100%', padding: '8px 10px', border: '1px solid #d1d5db', borderRadius: 6, fontSize: 14, boxSizing: 'border-box' }}
                          />
                        </div>
                        <div style={{ marginBottom: 10 }}>
                          <label style={{ fontSize: 13, color: '#6b7280', display: 'block', marginBottom: 3 }}>Email</label>
                          <input
                            type="email"
                            value={editEmail}
                            onChange={(e) => setEditEmail(e.target.value)}
                            style={{ width: '100%', padding: '8px 10px', border: '1px solid #d1d5db', borderRadius: 6, fontSize: 14, boxSizing: 'border-box' }}
                          />
                        </div>
                        {editError && (
                          <div style={{ color: '#dc2626', fontSize: 13, marginBottom: 8 }}>{editError}</div>
                        )}
                        <div style={{ display: 'flex', gap: 8 }}>
                          <button
                            onClick={() => handleSaveStudent(s.id)}
                            disabled={actionLoading === 'editStudent'}
                            style={{ padding: '6px 16px', background: '#1e3a5f', color: '#fff', border: 'none', borderRadius: 6, fontSize: 13, cursor: 'pointer', opacity: actionLoading === 'editStudent' ? 0.6 : 1 }}
                          >
                            {actionLoading === 'editStudent' ? 'Saving…' : 'Save'}
                          </button>
                          <button
                            onClick={handleCancelEdit}
                            disabled={actionLoading === 'editStudent'}
                            style={{ padding: '6px 16px', background: '#f3f4f6', color: '#374151', border: '1px solid #d1d5db', borderRadius: 6, fontSize: 13, cursor: 'pointer' }}
                          >
                            Cancel
                          </button>
                        </div>
                        <div style={{ marginTop: 6 }}>
                          <div style={styles.studentDetail}>
                            {s.state_of_residence || 'State not set'}
                            {s.graduation_date
                              ? ` · Class of ${new Date(s.graduation_date).getFullYear()}`
                              : ''}
                          </div>
                        </div>
                      </div>
                    ) : (
                      <>
                        <div style={styles.studentAvatar}>
                          {s.full_name ? s.full_name.charAt(0).toUpperCase() : '?'}
                        </div>
                        <div style={{ flex: 1 }}>
                          <div style={styles.studentName}>{s.full_name}</div>
                          <div style={{ fontSize: 13, color: '#6b7280', marginBottom: 2 }}>{s.email}</div>
                          <div style={styles.studentDetail}>
                            {s.state_of_residence || 'State not set'}
                            {s.graduation_date
                              ? ` · Class of ${new Date(s.graduation_date).getFullYear()}`
                              : ''}
                          </div>
                        </div>
                        <button
                          onClick={() => handleEditStudent(s)}
                          style={{ padding: '4px 12px', background: 'transparent', color: '#1e3a5f', border: '1px solid #d1d5db', borderRadius: 6, fontSize: 12, cursor: 'pointer', whiteSpace: 'nowrap' }}
                        >
                          Edit
                        </button>
                      </>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Retention Modal Overlay */}
      {showRetentionModal && (
        <div style={styles.overlay}>
          <div style={styles.modal}>
            <h2 style={styles.modalTitle}>Before you go…</h2>
            <p style={styles.modalText}>
              We'd hate to see you leave. As a thank you for being a member,
              we'd like to offer you <strong>50% off your next renewal</strong>.
            </p>

            <div style={styles.offerBox}>
              <div style={styles.offerPrice}>
                {account?.planType === 'family' ? '$48.50' : '$23.50'}
                <span style={styles.offerPer}>/yr</span>
              </div>
              <div style={styles.offerOriginal}>
                instead of {account?.planType === 'family' ? '$97' : '$47'}/yr
              </div>
            </div>

            <div style={styles.modalActions}>
              <button
                onClick={handleAcceptOffer}
                disabled={actionLoading === 'offer'}
                style={styles.acceptOfferBtn}
              >
                {actionLoading === 'offer' ? 'Applying…' : 'Accept 50% Off'}
              </button>
              <button
                onClick={confirmCancel}
                disabled={actionLoading === 'cancel'}
                style={styles.declineBtn}
              >
                {actionLoading === 'cancel' ? 'Canceling…' : 'No thanks, cancel my subscription'}
              </button>
            </div>

            <button
              onClick={() => setShowRetentionModal(false)}
              style={styles.modalCloseBtn}
            >
              ✕
            </button>
          </div>
        </div>
      )}
    </>
  );
}

const styles = {
  page: {
    minHeight: 'calc(100vh - 56px)',
    background: '#F3F4F6',
    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
    padding: '24px 16px',
  },
  container: {
    maxWidth: '640px',
    margin: '0 auto',
  },
  loadingText: {
    textAlign: 'center',
    color: '#6B7280',
    fontSize: '14px',
    paddingTop: '100px',
  },
  success: {
    background: '#D1FAE5',
    border: '1px solid #6EE7B7',
    borderRadius: '10px',
    padding: '14px 18px',
    color: '#065F46',
    fontSize: '14px',
    marginBottom: '16px',
    fontWeight: 500,
  },
  error: {
    background: '#FEF2F2',
    border: '1px solid #FECACA',
    borderRadius: '10px',
    padding: '14px 18px',
    color: '#DC2626',
    fontSize: '14px',
    marginBottom: '16px',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  dismissBtn: {
    background: 'none',
    border: 'none',
    color: '#DC2626',
    cursor: 'pointer',
    fontSize: '16px',
    padding: '0 4px',
  },
  cancelBanner: {
    background: '#FEF3C7',
    border: '1px solid #FCD34D',
    borderRadius: '10px',
    padding: '16px 20px',
    marginBottom: '16px',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: '16px',
    flexWrap: 'wrap',
  },
  cancelBannerText: {
    display: 'flex',
    flexDirection: 'column',
    gap: '4px',
    color: '#92400E',
    fontSize: '14px',
  },
  reactivateBtn: {
    padding: '10px 20px',
    background: '#1F4E79',
    color: 'white',
    border: 'none',
    borderRadius: '8px',
    fontSize: '14px',
    fontWeight: 600,
    cursor: 'pointer',
    whiteSpace: 'nowrap',
  },
  card: {
    background: '#fff',
    borderRadius: '12px',
    padding: '24px',
    boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
    marginBottom: '16px',
  },
  cardTitleRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '20px',
  },
  cardTitle: {
    fontSize: '17px',
    fontWeight: 600,
    color: '#111827',
    margin: 0,
    marginBottom: '20px',
  },
  statusBadge: {
    fontSize: '12px',
    fontWeight: 600,
    padding: '4px 12px',
    borderRadius: '99px',
  },
  detailGrid: {
    display: 'flex',
    flexDirection: 'column',
    gap: '14px',
  },
  detailRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  detailLabel: {
    fontSize: '14px',
    color: '#6B7280',
    fontWeight: 500,
  },
  detailValue: {
    fontSize: '14px',
    color: '#111827',
    fontWeight: 500,
    textAlign: 'right',
  },
  discountNote: {
    fontSize: '12px',
    color: '#059669',
    fontWeight: 400,
  },
  cancelSection: {
    borderTop: '1px solid #F3F4F6',
    marginTop: '20px',
    paddingTop: '20px',
  },
  cancelBtn: {
    padding: '10px 20px',
    background: 'none',
    color: '#DC2626',
    border: '1px solid #FECACA',
    borderRadius: '8px',
    fontSize: '14px',
    fontWeight: 500,
    cursor: 'pointer',
    width: '100%',
  },
  studentList: {
    display: 'flex',
    flexDirection: 'column',
    gap: '12px',
  },
  studentRow: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    padding: '10px 12px',
    background: '#F9FAFB',
    borderRadius: '8px',
  },
  studentAvatar: {
    width: '36px',
    height: '36px',
    borderRadius: '50%',
    background: '#E5E7EB',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '15px',
    fontWeight: 700,
    color: '#374151',
    flexShrink: 0,
  },
  studentName: {
    fontSize: '14px',
    fontWeight: 600,
    color: '#111827',
  },
  studentDetail: {
    fontSize: '12px',
    color: '#6B7280',
    marginTop: '2px',
  },
  // Modal styles
  overlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: 'rgba(0, 0, 0, 0.5)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 200,
    padding: '20px',
  },
  modal: {
    background: 'white',
    borderRadius: '16px',
    padding: '32px',
    maxWidth: '440px',
    width: '100%',
    position: 'relative',
    boxShadow: '0 20px 60px rgba(0,0,0,0.15)',
  },
  modalTitle: {
    fontSize: '22px',
    fontWeight: 700,
    color: '#111827',
    margin: '0 0 12px 0',
    textAlign: 'center',
  },
  modalText: {
    fontSize: '15px',
    color: '#4B5563',
    lineHeight: 1.6,
    textAlign: 'center',
    margin: '0 0 24px 0',
  },
  offerBox: {
    background: '#F0FDF4',
    border: '2px solid #86EFAC',
    borderRadius: '12px',
    padding: '20px',
    textAlign: 'center',
    marginBottom: '24px',
  },
  offerPrice: {
    fontSize: '36px',
    fontWeight: 700,
    color: '#059669',
  },
  offerPer: {
    fontSize: '16px',
    fontWeight: 400,
    color: '#6B7280',
  },
  offerOriginal: {
    fontSize: '14px',
    color: '#6B7280',
    marginTop: '4px',
    textDecoration: 'line-through',
  },
  modalActions: {
    display: 'flex',
    flexDirection: 'column',
    gap: '10px',
  },
  acceptOfferBtn: {
    padding: '14px 20px',
    background: '#059669',
    color: 'white',
    border: 'none',
    borderRadius: '10px',
    fontSize: '16px',
    fontWeight: 600,
    cursor: 'pointer',
    width: '100%',
  },
  declineBtn: {
    padding: '12px 20px',
    background: 'none',
    color: '#6B7280',
    border: 'none',
    borderRadius: '10px',
    fontSize: '14px',
    cursor: 'pointer',
    width: '100%',
    textDecoration: 'underline',
  },
  modalCloseBtn: {
    position: 'absolute',
    top: '12px',
    right: '16px',
    background: 'none',
    border: 'none',
    fontSize: '20px',
    color: '#9CA3AF',
    cursor: 'pointer',
    padding: '4px',
  },
};